import React, { useEffect, useState } from 'react';
import TempleCard from '../components/TempleCard';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import { getTemples, getOtherTemples } from '../services/api';
import { Flower } from 'lucide-react';
import { generateBreadcrumbSchema, generateHomeItemListSchema } from '../utils/schemaGenerator';

const Temples = () => {
  const [temples, setTemples] = useState([]);
  const [otherTemples, setOtherTemples] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const [tData, oData] = await Promise.all([getTemples(), getOtherTemples()]);
      setTemples(tData);
      setOtherTemples(oData);
      setLoading(false);
    };
    fetchData();
  }, []);

  // Generate schemas once data is loaded (use temples and otherTemples)
  const allTemples = [...temples, ...otherTemples];
  const itemListSchema = generateHomeItemListSchema(allTemples);
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Temples', url: '/temples' }
  ]);

  const rawSchemas = [breadcrumbSchema, itemListSchema].filter(Boolean);

  return (
    <div className="bg-stone-50 min-h-screen pt-12 pb-24 border-t border-stone-200">
      <SEO 
        title="Explore All Temples" 
        description="Browse the complete directory of historical and spiritual Nathnagri temples in Bareilly." 
        keywords="temples directory, bareilly temples, explore shrines" 
        schema={rawSchemas}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-4">
            <div className="bg-saffron-100 p-3 rounded-full">
              <Flower className="h-8 w-8 text-saffron-600" />
            </div>
          </div>
          <h1 className="text-4xl font-extrabold text-stone-900 tracking-tight mb-4">
            <span className="text-saffron-600">नाथ नगरी</span> के मंदिर
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            उन सात प्राचीन मंदिरों की खोज करें जो बरेली को इसकी आध्यात्मिक पहचान देते हैं। इतिहास पढ़ें, समय देखें और अपनी यात्रा की योजना बनाएं।
          </p>
        </div>

        <div className="mb-12">
          <AdBanner placement="banner" pageName="temples" />
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className="bg-white rounded-2xl h-[400px] border border-stone-100 shadow-sm animate-pulse flex flex-col">
                <div className="h-56 bg-stone-200 rounded-t-2xl"></div>
                <div className="p-5 flex-1 space-y-3">
                  <div className="h-6 bg-stone-200 rounded w-1/3"></div>
                  <div className="h-4 bg-stone-200 rounded w-1/4"></div>
                  <div className="space-y-2 mt-4 text-sm text-stone-600">
                    <div className="h-4 bg-stone-200 rounded w-full"></div>
                    <div className="h-4 bg-stone-200 rounded w-5/6"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 pb-10">
              {temples.map((temple, index) => (
                <TempleCard key={`nath-${temple.id}`} temple={temple} index={index} />
              ))}
            </div>

            {otherTemples.length > 0 && (
              <div className="pt-16 border-t border-stone-200">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-extrabold text-stone-900 tracking-tight">
                    अन्य नाथ नगरी मंदिर (Other NathNagri Temples)
                  </h2>
                  <p className="text-stone-600 mt-3 max-w-2xl mx-auto">बरेली के कुछ अन्य प्रसिद्ध और ऐतिहासिक दर्शनीय मंदिर।</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {otherTemples.map((temple, index) => (
                    <TempleCard key={`other-${temple.id}`} temple={temple} index={index + temples.length} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default Temples;
